// ── THE PREVENT-PLANT DETERMINATION ──────────────────────────────────────────
//
// Kevin, 2026-09-28: *"I need you to act as a digital adjuster… you need to process the
// claim basically as if you are an adjuster and generate what you think the farmer is
// owed… With prevent plant, for example, that can mean calculating added land factor,
// looking up eligible acres, rolling acres to another crop, etc."*
//
// This is the piece that turns a per-cell arithmetic into a determination. Everything
// under it already existed and nothing joined it up:
//
//   src/claims/ppRate.js          what one prevented acre pays (¶75(1)(a))
//   src/claims/ppEligibility.js   eligible acres, the added-land ratio, the roll
//   crop_coverage_units           the reported PP acres, APH, level and share
//   pp_eligibility                the hand-uploaded worksheet
//
// ⚠ ELIGIBILITY IS DETERMINED PER CROP, COUNTY AND TYPE AND SPENT ACROSS THE POLICY.
// ¶26C(9) reaches "another crop insured for the current CY", and ¶26C(9)(d) is explicit
// that the lender need not sit on the same ground — its own example lends from legal
// section 9 to section 12. So this function takes the WHOLE policy at once. Called per
// cell it could not roll at all, which is the difference between a claim that pays and
// one that does not.
//
// ⚠ IT REFUSES THE CARRIER'S OWN FACTOR. `resolvePpFactor` ranks the AIP's stored
// `acre_guarantee_qty` first — which is liability rearranged — and an estimate built on
// it agrees with the carrier by construction. `basis: "aip_stated"` is refused here for
// the same reason `modelledPpExpectation` refuses it: this is a CHECK, and a check that
// cannot disagree is not one.

import { acreGuaranteeQty, ppDollarsPerAcre, cents, dollars } from "./ppRate.js";
import { remainingEligibleAcres, allocatePreventedAcres } from "./ppEligibility.js";

const num = (v) => (v == null || v === "" || !isFinite(Number(v)) ? null : Number(v));
const up = (s) => String(s == null ? "" : s).trim().toUpperCase();
const acres2 = (v) => +(num(v) || 0).toFixed(2);

// ⚠ WHICH RULE PRODUCED A FROZEN ESTIMATE. `claim_estimates` is keyed on it, so two rows
// computed by two different rules can never be pooled into one accuracy claim — and the
// table's BEFORE UPDATE trigger means a second opinion is a NEW version, never an edit.
// CHANGE THIS WHENEVER THE DETERMINATION CHANGES: leaving it fixed while the arithmetic
// moves is how a track record comes to describe a rule that no longer exists.
// ppAdjuster-3 (2026-10-19): the farm's other policies lend (same county, own policy first),
// one worksheet row per crop, prices per state. ppAdjuster-2: priced PER UNIT (own APH, level, share and practice price),
// E acres taken as AIP-verified eligible, prevented-but-uninsurable lines in neither bucket.
export const ADJUDICATION_VERSION = "ppAdjuster-3";

export const NOT_EVALUATED = Object.freeze([
  Object.freeze({ what: "added land",
    why: "¶26C(1)(b) can raise eligible acres when this year's cropland is greater than last "
       + "year's, with proof of how it was acquired. Neither year's total cropland is on file, "
       + "so added land is not evaluated — eligibility is the worksheet's figure unchanged." }),
  Object.freeze({ what: "second crop on prevented acres",
    why: "a second crop planted on prevented acreage cuts the payment to 35% unless the "
       + "double-crop history is met (7 CFR 457.8 §15(f)(2), §15(h)). Nothing we hold says "
       + "which prevented acres went to a second crop, so this figure assumes none did." }),
]);

// One cell = one (county, crop, type). That is the grain eligibility is determined on
// and the grain the worksheet is keyed on.
export const cellKey = (county, crop, type) =>
  [up(county), up(crop), up(type) || "(no type)"].join("|");

// ── WHAT IS ELIGIBLE, AND HOW WE KNOW ────────────────────────────────────────
//
// Two routes, and which one answered is reported on every cell:
//
//   stated   the worksheet's own "Remaining Eligible Acres" — the AIP's ¶26C(4)
//            determination, and the ONLY source for it. `planted_acres` is populated on
//            172 of 1,214 rows, so we usually cannot net it ourselves.
//   derived  max_acres less planted acres we hold, per ¶26C(4).
//
// ⚠ AND A CELL WE CANNOT ANSWER FOR IS `unknown`, NEVER ZERO. Zero eligible acres is a
// real determination — it forces every prevented acre on that crop to roll — and a
// worksheet nobody has uploaded must not impersonate it. An unknown cell lends nothing
// and blocks nothing; it is reported.
export function resolveEligibility(eligRow, { plantedAcres = null } = {}) {
  if (!eligRow) {
    return { acres: null, basis: "unknown", why: "no prevent-plant worksheet is loaded for this crop and county" };
  }
  const stated = num(eligRow.remaining_acres);
  if (stated != null && stated >= 0) {
    return { acres: stated, basis: "stated",
      why: `the worksheet states ${stated} remaining eligible acres (¶26C(4), the AIP's own determination)` };
  }
  const max = num(eligRow.max_acres);
  if (max == null) {
    return { acres: null, basis: "unknown",
      why: "the worksheet carries no maximum eligible acreage for this crop and county" };
  }
  // ⚠ A STORED ZERO IS NOT A STATED ZERO, AND THIS IS THE LANG FARMS DEFECT.
  // `pp_eligibility.planted_acres` is hand-extracted from the carrier's PP worksheet and
  // is 0 on 131 of the 654 rows that have a coverage to compare against — on 123 of those
  // the BOOK shows planted ground, so the 0 means "this column was not filled in", not
  // "nothing was planted". Preferred blindly it hands back the GROSS ceiling and the
  // whole ¶26C(4) reduction silently does not happen.
  //
  // LANG FARMS LLC CY2026 corn: worksheet max 72.6 with planted_acres 0, while the
  // acreage report says 36.7 planted + 36.0 prevented. Trusting the 0 makes eligibility
  // read 72.6, his 36.0 reported PP look comfortably inside it, and the carrier's $21
  // shortfall unexplainable. Using the book's 36.7 gives 35.9 remaining — which is the
  // AIP's own determined acreage on the loss record, to the tenth.
  //
  // ⚠ A GENUINE ZERO STILL WINS. A crop that was entirely prevented really does have 0
  // planted acres, so the zero is only overridden where the BOOK positively contradicts
  // it. Where the book says nothing, the worksheet stands — absence is not evidence.
  // Measured: 30 cells carry both a contradicted zero and prevent-plant acres, i.e. 30
  // cells where this decides an eligibility answer today.
  const wsPlanted = num(eligRow.planted_acres);
  const bookPlanted = num(plantedAcres);
  const zeroContradicted = wsPlanted === 0 && bookPlanted != null && bookPlanted > 0.05;
  const planted = zeroContradicted ? bookPlanted
    : (wsPlanted != null ? wsPlanted : bookPlanted);
  const r = remainingEligibleAcres({ base: max, plantedAcres: planted });
  if (!r.netted) {
    // ⚠ THE GROSS CEILING IS NOT A REMAINING FIGURE, and handing it over as one pays on
    // acres the crop already planted. Reported as its own basis so a screen can say so.
    return { acres: r.acres, basis: "gross_only", plantedExceedsBase: false,
      why: "¶26C(4) reduces eligible acres by the crop's own planted acres, and neither the "
         + "worksheet nor the book states them — this is the gross ceiling, not a remaining figure" };
  }
  return { acres: r.acres, basis: "derived", plantedExceedsBase: r.plantedExceedsBase,
    // The override is REPORTED, never silent: it changes an eligibility figure, and an
    // agent reading "35.9 eligible" needs to know it came from the acreage report rather
    // than the worksheet column that says 0.
    plantedFrom: zeroContradicted ? "acreage_report" : (wsPlanted != null ? "worksheet" : "acreage_report"),
    why: zeroContradicted
      ? `${r.why} — the worksheet's planted column reads 0, which the acreage report contradicts (${bookPlanted} ac planted), so the reported acreage is used`
      : r.why };
}

// ── THE DETERMINATION ────────────────────────────────────────────────────────
//
// `cells` is every (county, crop, type) on ONE POLICY — the prevented ones and the
// unprevented ones alike, because an unprevented crop with spare eligibility is exactly
// what a prevented one borrows from.
//
//   { county, crop, type, ppAcres, plantedAcres, aph, coverageLevel, share, eligRow }
//
// `model.factorFor(crop, type)` -> { factor, basis } and `model.priceFor(crop, type)`
// -> { price, basis } are supplied by the CALLER. This module must not reach for RMA's
// tables itself — App.jsx owns `resolvePpFactor` and `resolveRmaPrice`, and a second
// copy of either is how they drift.
// ── ONE RATE PATH ────────────────────────────────────────────────────────────
// Every prevent-plant cell the determination reads is priced HERE, through ppRate.js — for
// the claimant and every lender alike. Two paths pricing one acre is the drift to end.
export function priceCells(cells = [], model = {}) {
  const absences = [];
  const rows = [];

  // ── 1. price every cell, prevented or not ─────────────────────────────────
  // ⚠ ONE WORKSHEET ROW IS ONE CROP'S ELIGIBILITY, EVEN WHEN SEVERAL TYPES SHARE IT.
  // `pp_eligibility` joins on (policy, county, crop) — not type — so a corn GSG cell and a
  // corn GSS shell both arrive holding the SAME row, and each read it as its own: the
  // eligibility counted twice. BAINBRIDGE's empty silage line "lent" 234.83 acres of corn
  // eligibility to its own grain line (found 2026-10-19). The row now belongs to ONE cell —
  // the one claiming prevent plant, else the one with the most acres — and the others hold
  // none of their own (they can still borrow it as a same-crop lender).
  const primaryFor = new Map();
  (cells || []).forEach((c, i) => {
    if (!c || !c.eligRow) return;
    const w = (x) => [(num(x.ppAcres) || 0) > 0.005 ? 1 : 0, (num(x.ppAcres) || 0) + (num(x.plantedAcres) || 0)];
    const cur = primaryFor.get(c.eligRow);
    if (cur == null) { primaryFor.set(c.eligRow, i); return; }
    const a = w(c), b = w(cells[cur]);
    if (a[0] > b[0] || (a[0] === b[0] && a[1] > b[1])) primaryFor.set(c.eligRow, i);
  });
  (cells || []).forEach((c, idx) => {
    if (!c) return;
    const crop = up(c.crop), county = up(c.county), type = up(c.type);
    const f = (model.factorFor ? model.factorFor(crop, type) : null) || {};
    const p = (model.priceFor ? model.priceFor(crop, type) : null) || {};
    const factor = num(f.factor), price = num(p.price);

    // ⚠ REFUSED, NOT PREFERRED — see the header. A factor read back out of the AIP's own
    // stated guarantee cannot disagree with the AIP.
    const factorRefused = f.basis === "aip_stated";
    // ⚠ THE PRICE ELECTION IS RESOLVED, NOT ASSUMED. A grower may insure below the full
    // projected price (CAT is 50% coverage at 55% price), and omitting it prices a CAT
    // acre at 1.82x what the carrier pays — which is exactly how this rule's first live
    // comparison against the AIP failed, on every BAINBRIDGE ALLEGANY corn unit. A cell
    // with no election on file is left UNPRICED rather than assumed full: 17 coverages
    // sit at a CAT level with none recorded, so "not stated" may genuinely not be 100%.
    const priceElection = num(c.priceElectionPct);
    // ⚠ PRICED PER UNIT WHERE THE UNITS ARE GIVEN. One (county, crop, type) spans several
    // units with their own APH, coverage level and share, and the carrier prices each one.
    // A single cell APH priced every acre at one unit's yield — Krenzer's Monroe corn read
    // $5,479 high. So the cell's rate is the SHARE-weighted average of its units' own rates
    // and its share the acre-weighted average share: acres x rate x share then reproduces
    // Σ(unit acres x unit rate x unit share) exactly, while `rate` stays a PRE-share figure,
    // which is what ¶26C(9)(b)'s lower-of-two-rates comparison needs. Any unit that cannot
    // be priced leaves the whole cell unpriced — never totalled around.
    const pu = Array.isArray(c.ppUnits) ? c.ppUnits.filter((u) => (num(u.acres) || 0) > 0) : [];
    let rate = null, share = num(c.share), unitRates = null, eAcres = 0;
    if (!factorRefused) {
      if (pu.length) {
        let aw = 0, arsw = 0, asw = 0, ok = true;
        unitRates = [];
        for (const u of pu) {
          const a = num(u.acres), s = num(u.share);
          // The unit's own practice price where the assembly resolved one (an organic unit
          // beside a conventional one in the same cell), the cell's otherwise.
          const upx = num(u.price) != null ? num(u.price) : price;
          const r = ppDollarsPerAcre(u.aph, u.coverageLevel, factor, upx, priceElection);
          unitRates.push({ acres: a, aph: num(u.aph), coverageLevel: num(u.coverageLevel), share: s,
            status: u.status || null, perAcre: r,
            guaranteeQty: acreGuaranteeQty(u.aph, u.coverageLevel, factor) });
          if (r == null || s == null || s <= 0) { ok = false; continue; }
          aw += a; arsw += a * r * s; asw += a * s;
          // ⚠ `E` IS RMA'S "COMPANY-VERIFIED ELIGIBLE PP ACRES FROM ANOTHER UNIT" (ICE D00051).
          // The AIP has already verified their eligibility elsewhere, so they are NOT re-capped
          // against this crop's own worksheet — doing so cut PALLADINO AND CARLEY's corn from
          // the $18,372 paid to $7,448 and ANCHOR's wheat from $6,080 to $5,339 (CY2026).
          if (up(u.status) === "E") eAcres += a;
        }
        if (ok && aw > 0 && asw > 0) { rate = arsw / asw; share = asw / aw; }
      } else {
        rate = ppDollarsPerAcre(c.aph, c.coverageLevel, factor, price, priceElection);
      }
    }

    const sharedOwner = c.eligRow && primaryFor.get(c.eligRow) !== idx ? cells[primaryFor.get(c.eligRow)] : null;
    const elig = sharedOwner
      ? { acres: 0, basis: "shared", why: `this crop's worksheet eligibility is counted once, on its ${up(sharedOwner.type) || "other"} line` }
      : resolveEligibility(c.eligRow, { plantedAcres: c.plantedAcres });
    rows.push({
      key: cellKey(county, crop, type), county, crop, type,
      // Carried through so a frozen estimate can name the coverage it is for. Without it
      // the payment can never be matched back and the row is not worth writing.
      iifKey: c.iifKey || null,
      // ⚠ WHERE THE PREVENTED ACRES CAME FROM, carried through untouched. A cell whose acres
      // are the 578's (src/claims/provisional578.js — the AIP has not keyed them yet) is
      // labelled on every determination built from it, so a screen can never show it as a
      // keyed figure. It changes no arithmetic.
      provisional: c.provisional || null,
      ppAcres: acres2(c.ppAcres),
      share: share,
      aph: num(c.aph), coverageLevel: num(c.coverageLevel),
      ppFactor: factorRefused ? null : factor,
      factorBasis: factorRefused ? "refused_aip_stated" : (f.basis || null),
      projectedPrice: price, priceBasis: p.basis || null,
      priceElectionPct: priceElection,
      // One guarantee quantity where every unit shares it; null (and `unitRates` carries
      // each) where they differ, so a screen never prints one unit's figure for all.
      guaranteeQty: factorRefused ? null
        : (unitRates && unitRates.length
            ? (new Set(unitRates.map((u) => u.guaranteeQty)).size === 1 ? unitRates[0].guaranteeQty : null)
            : acreGuaranteeQty(c.aph, c.coverageLevel, factor)),
      unitRates,
      eVerifiedAcres: acres2(eAcres),
      // Prevent-plant-coded lines the AIP also coded uninsurable — no guarantee on this
      // crop line, so they are in neither the claim nor the planted acres. Counted, shown.
      uninsurablePpAcres: acres2(c.uninsurablePpAcres),
      perAcre: rate,
      eligibleAcres: elig.acres, eligibilityBasis: elig.basis, eligibilityWhy: elig.why,
      // Spent as the roll consumes it.
      remaining: elig.acres == null ? null : elig.acres,
    });

    if (factorRefused) absences.push({ cell: cellKey(county, crop, type), what: "prevent-plant factor",
      why: "the only factor on file is read back out of the AIP's own stated guarantee, which cannot disagree with them" });
    else if (factor == null) absences.push({ cell: cellKey(county, crop, type), what: "prevent-plant factor",
      why: "no published factor for this crop and year" });
    if (price == null) absences.push({ cell: cellKey(county, crop, type), what: "projected price",
      why: "RMA has published no projected price we can read for this crop and year" });
    if (priceElection == null) absences.push({ cell: cellKey(county, crop, type),
      what: "price election percentage",
      why: "the coverage records no price election, and a CAT acre insured at 55% priced as "
         + "100% overstates by 1.82x — so this cell is not priced rather than assumed full" });
    if (elig.basis === "unknown") absences.push({ cell: cellKey(county, crop, type), what: "eligible acres", why: elig.why });
  });

  return { rows, absences };
}

// WHY A CLAIMANT COULD NOT BE PRICED — named for the input that is actually missing.
// ⚠ "no factor or projected price" used to be the catch-all for every perAcre == null, and
// the per-unit path also leaves perAcre null when a prevented UNIT has no insured share
// (any unpriceable unit leaves the cell unpriced). MERRIMAC's ProAg Livingston corn
// (36-415-1050573, hand-loaded from the SOI) read "no factor or projected price" with
// factor 0.55 and $4.62 both resolved — its 72.5 PP acres carry share NULL. A reason that
// names the wrong input sends the reader to fix the wrong table.
export function unpricedWhy(r) {
  if (!r) return "not priced";
  if (r.factorBasis === "refused_aip_stated")
    return "no independent prevent-plant factor — the only one on file is the AIP's own";
  const noFactor = r.ppFactor == null, noPrice = r.projectedPrice == null;
  if (noFactor && noPrice) return "no prevent-plant factor and no projected price for this crop and year";
  if (noFactor) return "no prevent-plant factor for this crop and year";
  if (noPrice) return "no projected price for this crop and year";
  if (r.priceElectionPct == null) return "no price election recorded on the coverage — not assumed to be 100%";
  const units = Array.isArray(r.unitRates) ? r.unitRates : [];
  if (units.some((u) => u.perAcre == null)) return "a prevented unit is missing its APH or coverage level";
  return "no insured share recorded on the unit — not assumed to be 100%";
}

export function adjudicatePreventPlant({ cells = [], model = {}, policyNumber = null, farmLenders = null } = {}) {
  // ── 1. price every cell, prevented or not — through the one rate path ──────
  const { rows, absences } = priceCells(cells, model);

  // ── 2. who is claiming, and who can lend ──────────────────────────────────
  const claimants = rows.filter((r) => r.ppAcres > 0);
  if (!claimants.length) {
    return { available: false, determinations: [], absences,
      why: "no prevent-plant acres are reported on this policy", policyNumber };
  }

  // ⚠ A CLAIMANT WITH NO RATE CANNOT BE ADJUDICATED, AND IS NOT SILENTLY DROPPED. Its
  // acres are reported as undetermined, because a total that quietly excluded them
  // would read as the whole claim.
  const priced = claimants.filter((r) => r.perAcre != null && r.share != null && r.share > 0);
  const unpriced = claimants.filter((r) => !(r.perAcre != null && r.share != null && r.share > 0));

  // ⚠ ORDER MATTERS AND THE HANDBOOK DOES NOT FIX IT. When two crops both over-report,
  // whichever is adjudicated first takes the closest-paying lender, and the second takes
  // what is left — so the TOTAL depends on an order ¶26C(9) never states. Worked example:
  // corn needing 10 ac at $280 and soybeans needing 10 at $200, against wheat 10 at $240
  // and oats 10 at $150, pays $240+$150 one way round and $200+$150 the other.
  //
  // So: a deterministic order (largest shortfall first — OURS, not RMA's), and the
  // moment more than one crop has to borrow the determination is FLAGGED as
  // order-dependent rather than presented as the answer.
  // ⚠ EVERY CLAIMANT RESERVES ITS OWN ELIGIBILITY BEFORE ANYONE CAN BORROW IT, AND THIS
  // HAS TO HAPPEN IN A PASS OF ITS OWN. ¶26C(9) only reaches another crop's eligibility
  // for a crop that "does not have an adequate base" — a crop's own acres are its own,
  // and what it does not need is what it lends. Adjudicating claimants one at a time
  // without reserving first lets the FIRST one borrow acres a LATER one was always
  // entitled to: corn needing 20 of its own 50 and soybeans needing 40 with none of its
  // own, adjudicated soy-first, has soy take 40 of corn's 50 and leaves corn short on
  // ground it owned outright. Found by the checker, not by reading.
  const reservedOwn = new Map();
  for (const r of priced) {
    // E acres are already eligibility-verified by the AIP (D00051) — they are the cell's
    // own without spending this crop's worksheet eligibility; the rest is capped as before.
    const ev = Math.min(r.ppAcres, r.eVerifiedAcres || 0);
    const fromWs = r.remaining == null ? 0 : Math.min(r.ppAcres - ev, r.remaining);
    reservedOwn.set(r.key, ev + fromWs);
    if (r.remaining != null) r.remaining = Math.max(0, r.remaining - fromWs);
  }

  const shortfall = (r) => Math.max(0, r.ppAcres - (reservedOwn.get(r.key) || 0));
  const borrowers = priced.filter((r) => shortfall(r) > 0.005);
  // A pool row another policy of this farm has ALREADY drawn on is contested across
  // policies: which policy draws first is as unstated as which crop does.
  const ownCrops = new Set(rows.map((o) => o.crop));
  const pool = Array.isArray(farmLenders) ? farmLenders.filter((o) =>
    o.policyNumber !== policyNumber && !ownCrops.has(o.crop)) : [];
  let crossPolicyContested = false;
  const orderDependentLocal = borrowers.length > 1;
  const queue = [...priced].sort((a, b) => shortfall(b) - shortfall(a)
    || a.key.localeCompare(b.key));

  const determinations = [];
  let totalCents = 0;
  for (const r of queue) {
    // Everything else on the policy with eligibility left, priced, excluding itself.
    const candidates = rows
      .filter((o) => o.key !== r.key && o.perAcre != null && o.remaining != null && o.remaining > 0)
      .map((o) => ({
        crop: o.crop, unit: o.key, county: o.county, type: o.type,
        sameCrop: o.crop === r.crop,
        perAcre: o.perAcre,
        eligibleAcres: o.remaining,
      }))
      // ⚠ THE SAME FARM'S OTHER POLICIES, IN THE SAME COUNTY (Kevin, 2026-10-19: "they
      // likely rolled to wheat"). Wheat is usually written on its own policy, so a corn ->
      // wheat roll is invisible to a policy-only search. Offered only from a pool the CALLER
      // built from worksheets on file (`buildFarmLenderPool`), only for a crop this policy
      // does not itself carry (two policies on one crop share ONE eligibility — the MAX rule
      // — so lending it twice would invent acres), and paid at the lower rate like any roll.
      .concat(pool
        .filter((o) => o.county === r.county && o.perAcre != null && o.remaining != null && o.remaining > 0)
        .map((o) => ({
          crop: o.crop, unit: o.key, county: o.county, type: o.type,
          sameCrop: false, otherPolicy: true, perAcre: o.perAcre, eligibleAcres: o.remaining,
        })));

    // Contested across policies: this claimant could have used spare eligibility that
    // another of the farm's policies drew first (whether or not anything is left).
    if (shortfall(r) > 0.005 && pool.some((o) => o.county === r.county && o.drawnBy && o.drawnBy.size
        && !o.drawnBy.has(policyNumber))) crossPolicyContested = true;
    const alloc = allocatePreventedAcres({
      preventedAcres: r.ppAcres,
      preventedPerAcre: r.perAcre,
      // Its OWN acres, set aside in the reservation pass above — not `r.remaining`,
      // which by now holds only the surplus this cell can lend to others.
      preventedEligible: reservedOwn.get(r.key) || 0,
      share: r.share,
      candidates,
    });

    if (!alloc.available) {
      determinations.push({ ...r, available: false, missing: alloc.missing, indemnity: null });
      continue;
    }

    // Spend what was taken, so the next claimant sees a true remainder.
    for (const a of alloc.allocations || []) {
      // Own acres were already taken out of `remaining` in the reservation pass — taking
      // them again would let a later claimant see a surplus that does not exist.
      if (a.own) continue;
      const lender = rows.find((o) => o.key === a.unit);
      if (lender) { lender.remaining = Math.max(0, (lender.remaining || 0) - a.acres); continue; }
      const pl = pool.find((o) => o.key === a.unit);
      if (pl) {
        a.otherPolicy = pl.policyNumber;
        if (pl.drawnBy && pl.drawnBy.size && !pl.drawnBy.has(policyNumber)) crossPolicyContested = true;
        (pl.drawnBy = pl.drawnBy || new Set()).add(policyNumber);
        pl.remaining = Math.max(0, (pl.remaining || 0) - a.acres);
        if (pl.plantedNote) a.lenderNote = pl.plantedNote;
      }
    }

    totalCents += cents(alloc.indemnity);
    determinations.push({
      ...r, available: true,
      allocations: alloc.allocations,
      placedAcres: alloc.placedAcres,
      unplacedAcres: alloc.unplacedAcres,
      unplacedNote: alloc.unplacedNote,
      indemnity: alloc.indemnity,
      // Everything the figure was built from, so a screen can show its working without
      // re-deriving anything.
      why: `${r.ppAcres} ac reported · ${r.eligibleAcres == null ? "eligibility unknown" : r.eligibleAcres + " ac eligible"}`
         + (r.guaranteeQty != null
             ? ` · ${r.guaranteeQty} bu/ac guarantee x $${r.projectedPrice} x ${r.share} share`
             : ` · ${(r.unitRates || []).length} units priced separately at $${r.projectedPrice}`
               + ` (avg $${Number(r.perAcre).toFixed(2)}/ac before share)`)
         + (r.eVerifiedAcres > 0 ? ` · ${r.eVerifiedAcres} ac already verified eligible by the AIP (E)` : ""),
    });
  }

  return {
    available: true,
    policyNumber,
    determinations,
    // True when ANY prevented acre in this result is the 578's, not the AIP's keyed figure.
    provisional: determinations.some((d) => !!d.provisional),
    // ⚠ THE TOTAL COVERS ONLY WHAT WAS ADJUDICATED. Acres we could not price are counted
    // and named beside it, never folded in and never dropped.
    indemnity: dollars(totalCents),
    undetermined: unpriced.map((r) => ({ key: r.key, county: r.county, crop: r.crop,
      ppAcres: r.ppAcres,
      why: unpricedWhy(r) })),
    undeterminedAcres: acres2(unpriced.reduce((s, r) => s + r.ppAcres, 0)),
    unplacedAcres: acres2(determinations.reduce((s, d) => s + (d.unplacedAcres || 0), 0)),
    // ⚠ WHAT THIS DETERMINATION DOES NOT DO, NAMED ON EVERY RESULT. Each is a real rule
    // with no data source in what we hold, so it is HELD FOR A PERSON rather than modelled:
    // a guessed ratio or a guessed second crop would move a figure that gets frozen.
    notEvaluated: NOT_EVALUATED,
    orderDependent: orderDependentLocal || crossPolicyContested,
    // Acres borrowed from the farm's OTHER policies — named so a reader can check them.
    crossPolicyAcres: acres2(determinations.reduce((s, d) => s + (d.allocations || [])
      .filter((a) => a.otherPolicy).reduce((t, a) => t + a.acres, 0), 0)),
    orderNote: crossPolicyContested && !orderDependentLocal
      ? "another of this farm's policies drew on the same spare eligibility first; ¶26C(9) "
        + "does not state which policy draws first, so this figure depends on that order."
      : orderDependentLocal
      ? `${borrowers.length} crops on this policy each need more prevent-plant eligibility than `
        + `they hold, so they draw on the same spare acres. ¶26C(9) does not state which crop `
        + `draws first, and the order changes the rate each borrowed acre pays — this figure `
        + `uses largest-shortfall-first, which is our convention and not RMA's.`
      : null,
    absences,
  };
}

// ── THE DETERMINATION, ROLLED UP TO ONE (COUNTY, CROP) ───────────────────────
//
// The Claims cross-check compares per (policy, county, crop); the determination is made
// per (county, crop, TYPE) across the whole policy. This is the one place the two grains
// meet, so the cross-check's "We expect" column is THIS engine's answer and never a
// second formula beside it (that second formula existed until 2026-10-19 —
// `modelledPpExpectation` — and it had no eligibility cap, no roll, and read a missing
// price election as 100%).
//
// A (county, crop) gets a figure only when EVERY prevent-plant type under it was
// determined. Otherwise it is HELD with the reason, never totalled around:
//   - a claimant the engine could not price (no factor, price, share or election);
//   - a claimant whose eligibility is UNKNOWN and whose acres could not be placed —
//     no worksheet is not "zero eligible acres", and reading it as zero would print $0
//     as our expectation for a claim the carrier is about to pay.
// Acres that could not be placed against KNOWN eligibility are genuinely not payable
// (¶26C(9)) and stay out of the figure — that is a determination, not a gap.
//
// ⚠ ROLLED ACRES ARE CREDITED TO THE CROP THAT WAS PREVENTED, at the rate ¶26C(9)(b)
// sets. A carrier books them under the LENDING crop's line, so one cell can read high
// and its lender low while the POLICY ties — `rolled` is carried so a screen can say so.
//
// Returns Map("COUNTY|CROP" -> { figure, held, why, ppAcres, rolled, orderDependent }).
export function figuresByCountyCrop(out) {
  const res = new Map();
  if (!out || !out.available) return res;
  const cc = (county, crop) => up(county) + "|" + up(crop);
  const get = (county, crop) => {
    const k = cc(county, crop);
    if (!res.has(k)) res.set(k, { cents: 0, held: null, ppAcres: 0, rolled: false, parts: [] });
    return res.get(k);
  };
  for (const u of out.undetermined || []) {
    const e = get(u.county, u.crop);
    e.ppAcres += num(u.ppAcres) || 0;
    e.held = e.held || u.why;
  }
  for (const d of out.determinations || []) {
    const e = get(d.county, d.crop);
    e.ppAcres += num(d.ppAcres) || 0;
    if (!d.available) { e.held = e.held || ("missing: " + (d.missing || []).join(", ")); continue; }
    if (d.eligibilityBasis === "unknown" && (num(d.unplacedAcres) || 0) > 0.005) {
      e.held = e.held || ("eligibility unknown — " + (d.eligibilityWhy || "no prevent-plant worksheet")
        + "; " + d.unplacedAcres + " ac could not be placed");
      continue;
    }
    if ((d.allocations || []).some((a) => !a.own)) e.rolled = true;
    e.cents += cents(d.indemnity);
    e.parts.push(d.why);
  }
  for (const [k, e] of res) {
    res.set(k, {
      figure: e.held ? null : dollars(e.cents),
      held: e.held,
      ppAcres: +e.ppAcres.toFixed(2),
      rolled: e.rolled,
      orderDependent: !!out.orderDependent,
      why: e.held ? e.held : e.parts.join("; "),
    });
  }
  return res;
}

// ── THE FARM'S OTHER POLICIES AS LENDERS ─────────────────────────────────────
//
// `groups` is every policy of ONE farm for ONE crop year: [{ policy, cells, model }].
// The pool is built only from policies that report NO prevent-plant acres of their own —
// a policy that is itself claiming spends its own eligibility inside its own determination,
// and offering the same acres again here would let them be lent twice. Every row is a
// worksheet on file (`stated` or `derived` eligibility); an unknown or gross-only one lends
// nothing, because eligibility nobody has determined is not eligibility to borrow.
export function buildFarmLenderPool(groups = []) {
  const pool = [];
  for (const g of groups || []) {
    const cells = g.cells || [];
    if (cells.some((c) => (num(c.ppAcres) || 0) > 0.005)) continue;
    const pn = (g.policy && (g.policy.policy_number || g.policy.policyNumber)) || null;
    const { rows } = priceCells(cells, g.model || {});
    for (const r of rows) {
      if (!(r.eligibilityBasis === "stated" || r.eligibilityBasis === "derived")) continue;
      if (r.perAcre == null || !(r.remaining > 0)) continue;
      const cell = cells.find((c) => cellKey(c.county, c.crop, c.type) === r.key) || {};
      pool.push({ ...r, key: (pn || "?") + "|" + r.key, policyNumber: pn,
        plantedNote: cell.plantedReported ? null
          : "this policy reports no planted acres for the crop (a shell line), so its full "
          + "worksheet eligibility was available" });
    }
  }
  return pool;
}

// The whole farm, one crop year: each claiming policy adjudicated with the farm's other
// policies offered as lenders, in a fixed order (policy number) so the result is stable.
// Returns Map(policy id -> determination).
export function adjudicateFarm(groups = []) {
  const pool = buildFarmLenderPool(groups);
  const out = new Map();
  const claimants = (groups || []).filter((g) => (g.cells || []).some((c) => (num(c.ppAcres) || 0) > 0.005))
    .sort((a, b) => String((a.policy || {}).policy_number || "").localeCompare(String((b.policy || {}).policy_number || "")));
  for (const g of claimants) {
    const p = g.policy || {};
    out.set(p.id, adjudicatePreventPlant({ cells: g.cells, model: g.model,
      policyNumber: p.policy_number || null, farmLenders: pool }));
  }
  return out;
}
